import { Button, DynamicContent, IconRow } from 'components'

import { BsArrowRightSquareFill } from 'react-icons/bs'
import { BsCheckSquareFill } from 'react-icons/bs'
import { FaPeopleGroup } from 'react-icons/fa6'
import { BsHouse, BsMailboxFlag } from 'react-icons/bs'

import { useTranslation } from 'react-i18next'
import React, { useMemo } from 'react'
import { IconType } from 'react-icons'
import { useDynamicFormContent, useOneStop } from 'hooks'

const MAILIN_BALLOT_URL = '/mail'
const UPDATE_REGISTRATION_URL = '/register'
const FIND_POLLING_PLACE_URL = '/find/polls'
const MAILIN_BALLOT_RETURN_URL = '/find/return'

const GridItem: React.FC<{ label: string; text: any; icon: any }> = ({
  label,
  text,
  icon: Icon,
}) => {
  return (
    <div style={{ width: '100%', marginTop: 30 }}>
      <div
        style={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'flex-start',
          gap: 6,
        }}
      >
        {Icon}
        <h3 style={{ margin: 0, fontSize: 16 }}>{label}</h3>
      </div>
      <div style={{ marginLeft: 28, fontSize: 14, marginTop: 10 }}>{text}</div>
    </div>
  )
}

export const SuccessScreen: React.FC<{ matchedVoter: any }> = ({
  matchedVoter,
}) => {
  const {
    formContent,
    mailinBallotDeadlinePassed,
    registrationDeadlinePassed,
  } = useDynamicFormContent()
  const { t } = useTranslation(undefined, {
    keyPrefix: `statusLookupForm.successScreen`,
  })
  const { isOneStop, goToMailInBallot, goToPlanToVote, updateRegistration } =
    useOneStop()
  const getParty = () => {
    switch (matchedVoter.party) {
      case 'D':
        return t('parties.D')
      case 'R':
        return t('parties.R')
      default:
        return t('parties.I')
    }
  }

  const residentialAddress = useMemo(() => {
    const {
      residentialAddressNumber,
      residentialAddressStreet,
      residentialAddressCity,
      residentialAddressState,
      residentialAddressZip,
    } = matchedVoter
    return (
      <>
        {`${residentialAddressNumber} ${residentialAddressStreet}`}
        <br />
        {`${residentialAddressCity}, ${residentialAddressState} ${residentialAddressZip}`}
      </>
    )
  }, [matchedVoter])

  const mailingAddress = useMemo(() => {
    const {
      mailingAddress,
      mailingAddress2,
      mailingAddressCity,
      mailingAddressState,
      mailingAddressZip,
    } = matchedVoter
    if (!mailingAddress) {
      return null
    }
    return (
      <>
        {`${mailingAddress}`}
        {mailingAddress2 ? (
          <>
            <br /> {mailingAddress2}
          </>
        ) : null}
        <br />
        {`${mailingAddressCity}, ${mailingAddressState} ${mailingAddressZip}`}
      </>
    )
  }, [matchedVoter])
  const buttonWrapperStyle = { marginTop: 10, marginBottom: 20 }
  const overrideContent =
    formContent?.formOverrides?.['status-lookup']?.success?.bottom
  const dates = formContent?.dates
  return (
    <div>
      <IconRow
        icon={BsCheckSquareFill}
        iconColor="green"
        text={t('success')}
      ></IconRow>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          paddingRight: 60,
          paddingLeft: 80,
          marginTop: -14,
        }}
      >
        <GridItem
          label={t('gridItems.residentialAddress')}
          text={residentialAddress}
          icon={<BsHouse size={24} />}
        />
        <GridItem
          label={t('gridItems.mailingAddress')}
          text={mailingAddress ?? t('gridItems.sameAsMailing')}
          icon={<BsMailboxFlag size={24} />}
        />
        <GridItem
          label={t('gridItems.party')}
          text={getParty()}
          icon={<FaPeopleGroup size={20} />}
        />
        <div style={{ width: '50%' }}></div>
      </div>
      <div style={{ marginTop: 30 }}></div>
      <IconRow
        icon={BsArrowRightSquareFill}
        iconColor="blue"
        text={t(`makeAPlan`)}
      ></IconRow>

      {overrideContent ? (
        <DynamicContent data={overrideContent} />
      ) : (
        <>
          {mailinBallotDeadlinePassed && (
            <>
              <p>{t('aldreadyHaveMailinBallot')}</p>
              <div style={buttonWrapperStyle}>
                <Button
                  label={t('findBallotReturnSite')}
                  onClick={() => {
                    if (isOneStop) {
                      goToPlanToVote('return')
                    } else {
                      window.location.href = MAILIN_BALLOT_RETURN_URL
                    }
                  }}
                  fullWidth={false}
                />
              </div>{' '}
            </>
          )}
          {mailinBallotDeadlinePassed ? (
            <>
              <p>
                {t('noMailInBallot', {
                  electionDay: dates?.electionDay,
                })}
              </p>
              <div style={buttonWrapperStyle}>
                <Button
                  label={t('findMyPollingPlace')}
                  onClick={() => {
                    if (isOneStop) {
                      goToPlanToVote('polls')
                    } else {
                      window.location.href = FIND_POLLING_PLACE_URL
                    }
                  }}
                  fullWidth={false}
                />
              </div>
              <p
                dangerouslySetInnerHTML={{
                  __html: t('deadlinePassed', {
                    electionDay: dates?.electionDay,
                  }),
                }}
              />
            </>
          ) : (
            <div style={buttonWrapperStyle}>
              <Button
                label={t('requestMailinBallotButton')}
                onClick={() => {
                  if (isOneStop) {
                    goToMailInBallot()
                  } else {
                    window.location.href = MAILIN_BALLOT_URL
                  }
                }}
                fullWidth={false}
              />
            </div>
          )}
          {registrationDeadlinePassed ? (
            !mailinBallotDeadlinePassed && (
              <p
                dangerouslySetInnerHTML={{
                  __html: t(`deadlinePassedVoterRegistration`, {
                    electionDay: dates?.electionDay,
                  }),
                }}
                style={{ paddingTop: 20 }}
              />
            )
          ) : (
            <>
              <p style={{ textAlign: 'center', marginTop: 50 }}>
                {t(`needToChange`)}
              </p>
              <div style={buttonWrapperStyle}>
                <Button
                  label={t('updateRegistrationButton')}
                  onClick={() => {
                    if (isOneStop) {
                      updateRegistration()
                    } else {
                      window.location.href = UPDATE_REGISTRATION_URL
                    }
                  }}
                  fullWidth={false}
                />
              </div>
            </>
          )}

          <p
            style={{ fontSize: 12, marginTop: 60 }}
            dangerouslySetInnerHTML={{ __html: t('bottomNote') }}
          ></p>
        </>
      )}
    </div>
  )
}
